import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Pencil, Percent, Plus, Trash2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import type { RespuestaDeTasasDeMora, TasaDeMora } from '@/shared/api/tipos';
import { Cargando } from '@/shared/ui/Cargando';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { Interruptor } from '@/shared/ui/Interruptor';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { PestanasDelTarifario } from './PestanasDelTarifario';

const esquema = z.object({
  anio: z.coerce.number().int().min(2000).max(2100),
  trimestre: z.coerce.number().int().min(1).max(4),
  tasaMensual: z.coerce.number().min(0, 'No puede ser negativa').max(10, 'Revise el valor'),
  fuente: z.string().max(60),
});

type Formulario = z.infer<typeof esquema>;

const TRIMESTRES = [
  { valor: 1, nombre: 'Enero - Marzo' },
  { valor: 2, nombre: 'Abril - Junio' },
  { valor: 3, nombre: 'Julio - Septiembre' },
  { valor: 4, nombre: 'Octubre - Diciembre' },
];

export function PaginaTasasDeMora() {
  const clienteDeConsultas = useQueryClient();

  const [editando, setEditando] = useState<TasaDeMora | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<TasaDeMora | null>(null);
  const [error, setError] = useState<string | null>(null);

  const datos = useQuery({
    queryKey: ['tasas-mora'],
    queryFn: () => api.get<RespuestaDeTasasDeMora>('/tasas-mora'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['tasas-mora'] });
  }

  const cambiarCobro = useMutation({
    mutationFn: (activo: boolean) =>
      api.put<unknown>('/parametros/cobranza', {
        parametros: [{ clave: 'interes_mora_activo', valor: activo ? 'true' : 'false' }],
      }),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['parametros'] });
      refrescar();
    },
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el cobro de mora';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/tasas-mora/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Tasa de mora eliminada');
    },
    onError: (e) => {
      const mensaje = e instanceof ErrorDeApi ? e.message : 'No se pudo eliminar';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const tasas = datos.data?.tasas ?? [];
  const vigente = datos.data?.vigente ?? null;
  const cobroActivo = datos.data?.cobroActivo ?? false;

  const porAnio = new Map<number, TasaDeMora[]>();
  for (const t of tasas) {
    porAnio.set(t.anio, [...(porAnio.get(t.anio) ?? []), t]);
  }

  return (
    <>
      <EncabezadoDePagina
        icono={Percent}
        color="ambar"
        titulo="Interés por mora"
        subtitulo="Tasas del Banco Central por trimestre"
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nueva tasa"
            aria-label="Nueva tasa"
            className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      <PestanasDelTarifario />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div
        className={[
          'mb-6 flex flex-wrap items-center justify-between gap-4 rounded-2xl border-2 p-5',
          cobroActivo ? 'border-marca-300 bg-marca-50' : 'border-armazon-200 bg-armazon-50/60',
        ].join(' ')}
      >
        <div className="min-w-0">
          <p
            className={`text-sm font-bold uppercase ${
              cobroActivo ? 'text-marca-900' : 'text-armazon-700'
            }`}
          >
            Cobro de interés por mora
          </p>
          <p
            className={`mt-0.5 text-xs uppercase ${
              cobroActivo ? 'text-marca-700' : 'text-armazon-600'
            }`}
          >
            {cobroActivo
              ? 'Activo · se calcula interés en las facturas vencidas'
              : 'Inactivo · no se cobra interés hasta que se active'}
          </p>
        </div>
        <Interruptor
          activo={cobroActivo}
          alCambiar={() => cambiarCobro.mutate(!cobroActivo)}
          etiqueta="Cobro de interés por mora"
          deshabilitado={cambiarCobro.isPending}
        />
      </div>

      {vigente && (
        <div
          className={`mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5 ${
            cobroActivo ? '' : 'opacity-60'
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase text-amber-700">
                Tasa vigente · {vigente.nombreDelTrimestre} {datos.data?.anioActual}
              </p>
              <p className="importe mt-1 text-3xl font-bold text-amber-800">
                {vigente.tasaMensual.toFixed(3)} %
              </p>
              <p className="mt-1 text-xs uppercase text-amber-700">
                Mensual · fuente: {vigente.fuente}
              </p>

              {(vigente.anio !== datos.data?.anioActual ||
                vigente.trimestre !== datos.data?.trimestreActual) && (
                <p className="mt-2 rounded-lg bg-amber-100 px-2.5 py-1.5 text-[11px] uppercase text-amber-900">
                  No hay tasa de {datos.data?.anioActual} Q{datos.data?.trimestreActual}: se usa la
                  de {vigente.anio} Q{vigente.trimestre}
                </p>
              )}
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase text-armazon-600">Art. 21 Código Tributario</p>
              <p className="text-[11px] uppercase text-armazon-500">
                Autodeterminación del contribuyente
              </p>
            </div>
          </div>
        </div>
      )}

      <p className="mb-4 text-sm uppercase tracking-wide text-armazon-600">
        {tasas.length} {tasas.length === 1 ? 'tasa registrada' : 'tasas registradas'}
      </p>

      {datos.isPending ? (
        <Cargando titulo="Cargando tasas de mora" detalle="Obteniendo registros" alto="h-48" />
      ) : tasas.length === 0 ? (
        <div className="rounded-2xl border border-armazon-200 bg-white p-16 text-center">
          <Percent className="mx-auto mb-3 size-12 text-armazon-200" aria-hidden />
          <p className="text-sm font-medium uppercase text-armazon-500">Sin tasas registradas</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {[...porAnio.entries()].map(([anio, delAnio]) => (
            <section key={anio}>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-bold uppercase text-armazon-700">
                <span className="importe grid size-6 place-items-center rounded-md bg-amber-100 text-xs font-bold text-amber-700">
                  {String(anio).slice(-2)}
                </span>
                {anio}
              </h2>

              <div className="overflow-x-auto rounded-xl border border-armazon-200 bg-white shadow-sm">
                <table className="w-full text-sm">
                  <thead className="bg-armazon-50/80">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
                        Trimestre
                      </th>
                      <th className="px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
                        Tasa mensual
                      </th>
                      <th className="px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
                        Tasa anual (×12)
                      </th>
                      <th className="px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
                        Fuente
                      </th>
                      <th className="w-24 px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-armazon-500">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...delAnio]
                      .sort((a, b) => a.trimestre - b.trimestre)
                      .map((t) => {
                        const esVigente = vigente?.id === t.id;
                        return (
                          <tr
                            key={t.id}
                            className={`border-t border-armazon-100 ${
                              esVigente ? 'bg-amber-50/60' : 'hover:bg-armazon-50/60'
                            }`}
                          >
                            <td className="px-4 py-3">
                              <span className="text-xs font-medium uppercase text-armazon-700">
                                Q{t.trimestre} · {t.nombreDelTrimestre}
                              </span>
                              {esVigente && (
                                <span className="ml-2 rounded bg-amber-200 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-900">
                                  Vigente
                                </span>
                              )}
                            </td>
                            <td className="importe px-4 py-3 text-center text-lg font-bold text-armazon-900">
                              {t.tasaMensual.toFixed(3)} %
                            </td>
                            <td className="importe px-4 py-3 text-center text-armazon-500">
                              {t.tasaAnual.toFixed(3)} %
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-medium uppercase text-blue-600">
                                {t.fuente}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => setEditando(t)}
                                  title="Corregir"
                                  aria-label={`Corregir ${t.anio} Q${t.trimestre}`}
                                  className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                                >
                                  <Pencil className="size-[13px]" aria-hidden />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setAEliminar(t)}
                                  title="Eliminar"
                                  aria-label={`Eliminar ${t.anio} Q${t.trimestre}`}
                                  className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
                                >
                                  <Trash2 className="size-[13px]" aria-hidden />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}

      <FormularioDeTasa
        key={editando?.id ?? 'nueva'}
        abierto={creando || editando !== null}
        tasa={editando}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => {
          setAEliminar(null);
          setError(null);
        }}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar tasa de mora"
        descripcion="Ese trimestre pasará a calcularse con la tasa más reciente de la tabla:"
        detalle={aEliminar ? `${aEliminar.anio} Q${aEliminar.trimestre}` : undefined}
        error={eliminar.isError ? error : null}
      />
    </>
  );
}

function FormularioDeTasa({
  abierto,
  tasa,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  tasa: TasaDeMora | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = tasa !== null;
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const hoy = new Date();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      anio: tasa?.anio ?? hoy.getFullYear(),
      trimestre: tasa?.trimestre ?? Math.ceil((hoy.getMonth() + 1) / 3),
      tasaMensual: tasa?.tasaMensual ?? 0,
      fuente: tasa?.fuente ?? 'BCE',
    },
  });

  const mensual = watch('tasaMensual');

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);
    try {
      if (editando) {

        await api.put<TasaDeMora>(`/tasas-mora/${tasa.id}`, {
          tasaMensual: datos.tasaMensual,
          fuente: datos.fuente,
        });
      } else {
        await api.post<TasaDeMora>('/tasas-mora', datos);
      }

      alGuardar();
      alCerrar();
      notificar.exito(`Tasa de mora ${editando ? 'actualizada' : 'guardada'}`);
    } catch (causa) {
      setErrorGeneral(causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la tasa');
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar la tasa');
    }
  });

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Corregir tasa' : 'Nueva tasa de interés'}
      descripcion={
        editando ? `${tasa.anio} Q${tasa.trimestre}` : 'Tasa mensual del BCE por mora tributaria'
      }
      icono={Percent}
      alEnviar={enviar}
      enviando={isSubmitting}
    >
      {errorGeneral && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {errorGeneral}
        </p>
      )}

      {editando ? (
        <div className="rounded-lg border border-armazon-200 bg-armazon-50 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wide text-armazon-600">Trimestre</p>
          <p className="text-sm font-semibold uppercase text-armazon-900">
            {tasa.anio} · Q{tasa.trimestre} · {tasa.nombreDelTrimestre}
          </p>
          <p className="mt-1 text-[11px] uppercase text-armazon-500">
            No se cambia: identifica la tasa
          </p>
        </div>
      ) : (
        <DosColumnas>
          <CampoDelPanel etiqueta="Año" requerido error={errors.anio?.message}>
            <input
              {...register('anio')}
              type="number"
              min={2000}
              max={2100}
              className={`${claseEntradaPanel} importe`}
            />
          </CampoDelPanel>

          <CampoDelPanel etiqueta="Trimestre" requerido error={errors.trimestre?.message}>
            <select {...register('trimestre')} className={claseEntradaPanel}>
              {TRIMESTRES.map((t) => (
                <option key={t.valor} value={t.valor}>
                  Q{t.valor} · {t.nombre}
                </option>
              ))}
            </select>
          </CampoDelPanel>
        </DosColumnas>
      )}

      <CampoDelPanel
        etiqueta="Tasa mensual (%)"
        requerido
        error={errors.tasaMensual?.message}
        ayuda={mensual ? `Anual: ${(Number(mensual) * 12).toFixed(3)} %` : 'La que publica el BCE'}
      >
        <input
          {...register('tasaMensual')}
          type="number"
          step="0.001"
          min={0}
          max={10}
          placeholder="0.647"
          className={`${claseEntradaPanel} importe text-center text-xl font-bold`}
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Fuente"
        error={errors.fuente?.message}
        ayuda="Para justificar el cobro si alguien lo reclama"
      >
        <input {...register('fuente')} placeholder="BCE" className={claseEntradaPanel} />
      </CampoDelPanel>
    </PanelLateral>
  );
}
